<script setup>
import { computed } from 'vue';

const props = defineProps({
  // valor atual (ex.: 0.92 para 0,92 gb)
  value: {
    type: Number,
    default: 0,
  },
  // valor maximo de referencia da barra (ex.: 10 para 10 gb)
  max: {
    type: Number,
    default: 100,
  },
  // cor do bootstrap utilizada na barra (bg-{variant})
  variant: {
    type: String,
    default: 'primary',
  },
  // altura da barra (o tema define .857rem no .progress)
  height: {
    type: String,
    default: '1.25rem',
  },
  // casas decimais exibidas no percentual
  decimal: {
    type: Number,
    default: 2,
  },
  showPercentage: {
    type: Boolean,
    default: true,
  },
  // texto complementar exibido apos o percentual (ex.: "de 10 GB")
  label: {
    type: String,
    default: '',
  },
});

const valorAtual = computed(() => {
  const valor = Number(props.value);
  return Number.isFinite(valor) && valor > 0 ? valor : 0;
});

const valorMaximo = computed(() => {
  const valor = Number(props.max);
  return Number.isFinite(valor) && valor > 0 ? valor : 0;
});

// percentual real de uso (pode passar de 100 quando o valor atual e maior que o maximo)
const percentual = computed(() => {
  if (!valorMaximo.value) return 0;
  return (valorAtual.value / valorMaximo.value) * 100;
});

// largura da barra limitada entre 0% e 100%
const larguraBarra = computed(() => Math.min(Math.max(percentual.value, 0), 100));

const percentualLabel = computed(
  () => `${percentual.value.toLocaleString('pt-BR', {
    minimumFractionDigits: props.decimal,
    maximumFractionDigits: props.decimal,
  })}%`,
);

const progressStyle = computed(() => `height: ${props.height}`);

const barClass = computed(() => ['progress-bar', `bg-${props.variant}`]);

const barStyle = computed(() => `width: ${larguraBarra.value}%`);

const ariaLabel = computed(() => (props.label ? `${percentualLabel.value} ${props.label}` : percentualLabel.value));
</script>

<template>
  <div class="progress-bar-app d-flex align-items-center">
    <div class="progress flex-grow-1" :style="progressStyle" role="progressbar" :aria-valuenow="larguraBarra" aria-valuemin="0" aria-valuemax="100" :aria-label="ariaLabel">
      <div :class="barClass" :style="barStyle"></div>
    </div>
    <span v-if="showPercentage" class="progress-bar-app-percentual fw-bold">{{ percentualLabel }}</span>
    <span v-if="label" class="ms-1 text-nowrap text-muted">{{ label }}</span>
  </div>
</template>

<style scoped>
.progress-bar-app {
  --progress-bar-track-color: rgba(237, 242, 249);
}

.dark-layout .progress-bar-app {
  --progress-bar-track-color: #3b4253;
}

.progress-bar-app .progress {
  background-color: var(--progress-bar-track-color);
}

.progress-bar {
  transition: width 0.3s ease;
}

.progress-bar-app-percentual {
  min-width: 4rem;
  text-align: right;
}
</style>
